import jsPDF from 'jspdf';
import QRCode from 'qrcode';

export const CODE_LABEL_DIMENSIONS_MM = {
  small: { width: 50, height: 25 },
  large: { width: 150, height: 100 },
} as const;

export type CodeLabelSize = keyof typeof CODE_LABEL_DIMENSIONS_MM;

interface CodeLabelData {
  code: string;
}

interface GenerateCodeLabelsPdfOptions {
  labels: CodeLabelData[];
  labelSize: CodeLabelSize;
  fileName: string;
}

const QR_PARALLEL_CHUNK = 12;

function qrBitmapWidthPx(qrSizeMm: number): number {
  return Math.min(512, Math.max(160, Math.round(qrSizeMm * 10)));
}

async function generateQrDataUrlsInChunks(labels: CodeLabelData[], qrPixelWidth: number): Promise<string[]> {
  const opts: QRCode.QRCodeToDataURLOptions = {
    width: qrPixelWidth,
    margin: 0,
    color: { dark: '#000000', light: '#FFFFFF' },
    errorCorrectionLevel: 'M',
  };

  const urls: string[] = [];
  for (let i = 0; i < labels.length; i += QR_PARALLEL_CHUNK) {
    const slice = labels.slice(i, i + QR_PARALLEL_CHUNK);
    const chunk = await Promise.all(slice.map((label) => QRCode.toDataURL(String(label.code), opts)));
    urls.push(...chunk);
  }
  return urls;
}

function drawLabelOnCurrentPage(pdf: jsPDF, label: CodeLabelData, w: number, h: number, qrDataUrl: string): void {
  const marginX = Math.max(2, w * 0.04);
  const marginY = Math.max(1.5, h * 0.06);
  const gap = Math.max(2, w * 0.04);
  const qrSize = Math.min(h - marginY * 2, w * 0.42);
  const textX = marginX;
  const textAreaW = w - marginX * 2 - gap - qrSize;
  const qrX = w - marginX - qrSize;
  const qrY = (h - qrSize) / 2;

  // Mantiene el diseño horizontal de etiqueta: texto a la izquierda y QR a la derecha.
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');

  const baseFontSize = h * 0.32;
  pdf.setFontSize(baseFontSize);

  const lines = pdf.splitTextToSize(label.code, textAreaW).slice(0, 3);
  const maxTextHeight = h - marginY * 2;
  const lineHeightRatio = 0.5;
  const fontSize = Math.min(baseFontSize, maxTextHeight / (1 + (lines.length - 1) * lineHeightRatio));
  const lineH = fontSize * lineHeightRatio;
  const totalTextH = fontSize + (lines.length - 1) * lineH;
  const textY = (h - totalTextH) / 2 + fontSize * 0.72;

  pdf.setFontSize(fontSize);
  lines.forEach((line: string, i: number) => {
    pdf.text(String(line), textX, textY + i * lineH, { align: 'left' });
  });

  pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
}

export async function generateCodeLabelsPdf({
  labels,
  labelSize,
  fileName,
}: GenerateCodeLabelsPdfOptions): Promise<void> {
  if (labels.length === 0) return;

  const { width: w, height: h } = CODE_LABEL_DIMENSIONS_MM[labelSize];
  const qrSizeMm = Math.min(h - Math.max(1.5, h * 0.06) * 2, w * 0.42);
  const qrPixelWidth = qrBitmapWidthPx(qrSizeMm);
  const qrDataUrls = await generateQrDataUrlsInChunks(labels, qrPixelWidth);

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [w, h],
  });

  for (let i = 0; i < labels.length; i++) {
    if (i > 0) {
      pdf.addPage([w, h], 'landscape');
    }
    drawLabelOnCurrentPage(pdf, labels[i], w, h, qrDataUrls[i]);
  }

  pdf.save(fileName);
}
